"use client"

import React from "react"
import { Box, Flex, Heading, Text, Card, Grid, Container } from "@radix-ui/themes"
import { Mail, Phone, MapPin, Clock } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import TurboLogo from "../components/TurboLogo"
import LanguageSwitcher from "../components/LanguageSwitcher"
import { Link } from "react-router-dom"

const ContactPage: React.FC = () => {
    const { t, dir, language } = useLanguage()

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col" dir={dir}>
            {/* Header */}
            <header className="bg-white border-b border-gray-200">
                <Container size="4">
                    <Flex justify="between" align="center" py="4" px="4">
                        <Link to="/" className="flex items-center gap-2">
                            <TurboLogo width={40} height={32} />
                            <Text size="5" weight="bold" className="hidden sm:block">
                                {t('turboPlatform')}
                            </Text>
                        </Link>
                        <div className="flex items-center gap-4">
                            <Link to="/" className="text-gray-600 hover:text-red-600 transition-colors text-sm font-medium">
                                {t('backToMainSite')}
                            </Link>
                            <LanguageSwitcher />
                        </div>
                    </Flex>
                </Container>
            </header>

            <div className="flex-1">
                {/* Hero Section */}
                <div className="bg-[#111] text-white py-20 relative overflow-hidden">
                    <div className="absolute inset-0 z-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1596524430615-b46476ddc820?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center" />
                    <div className="absolute inset-0 z-0 bg-gradient-to-b from-transparent to-black/80" />

                    <Container size="3" className="relative z-10 px-4 text-center">
                        <Heading size="8" mb="4">{t('contactUs')}</Heading>
                        <Text size="5" className="text-gray-300 max-w-2xl mx-auto">
                            We're here to help. Reach out to us for any questions or support.
                        </Text>
                    </Container>
                </div>

                <Container size="3" px="4" className="-mt-16 relative z-20 pb-20">
                    <Grid columns={{ initial: '1', md: '3' }} gap="6">
                        {/* Contact Cards */}
                        <Card size="3" className="hover:shadow-lg transition-shadow">
                            <Flex direction="column" align="center" gap="4" p="4" className="text-center">
                                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                                    <Mail size={24} />
                                </div>
                                <Heading size="4">Email Us</Heading>
                                <Text color="gray">
                                    For general inquiries and support
                                </Text>
                                <Text weight="bold" size="4" className="text-red-600">
                                    <a href="mailto:support@turbo.sd">support@turbo.sd</a>
                                </Text>
                            </Flex>
                        </Card>

                        <Card size="3" className="hover:shadow-lg transition-shadow">
                            <Flex direction="column" align="center" gap="4" p="4" className="text-center">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                    <Phone size={24} />
                                </div>
                                <Heading size="4">Call Us</Heading>
                                <Text color="gray">
                                    Speak directly with our support team
                                </Text>
                                <Flex direction="column" gap="2">
                                    <Text weight="bold" size="4">
                                        <a href="tel:+249100633522" className="text-blue-600 hover:underline" dir="ltr">+249 100 633 522</a>
                                    </Text>
                                    <Text weight="bold" size="4">
                                        <a href="tel:+249909103353" className="text-blue-600 hover:underline" dir="ltr">+249 909 103 353</a>
                                    </Text>
                                </Flex>
                            </Flex>
                        </Card>

                        <Card size="3" className="hover:shadow-lg transition-shadow">
                            <Flex direction="column" align="center" gap="4" p="4" className="text-center">
                                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                    <MapPin size={24} />
                                </div>
                                <Heading size="4">Visit Us</Heading>
                                <Text color="gray">
                                    Come say hello at our office
                                </Text>
                                <Text align="center">
                                    Khartoum, Sudan<br />
                                    Al-Amarat, Street 15
                                </Text>
                            </Flex>
                        </Card>
                    </Grid>

                    {/* Map or additional content could go here */}
                    <Box mt="9" className="text-center">
                        <Text color="gray" size="2">
                            © {new Date().getFullYear()} Turbo Platform. All rights reserved.
                        </Text>
                    </Box>
                </Container>
            </div>
        </div>
    )
}

export default ContactPage
